/**
 * ConfirmEmailChangePage — confirme un changement d'adresse email.
 *
 * L'utilisateur arrive ici via le lien reçu à sa NOUVELLE adresse. Le token est
 * consommé côté serveur ; l'email est basculé uniquement à ce moment. Anti-
 * enfermement : tant que ce lien n'est pas cliqué, l'ancienne adresse reste active.
 */

import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { PublicLayout } from '@/components/PublicLayout';
import { api, ApiException } from '@/lib/api';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

type Status = 'loading' | 'success' | 'invalid' | 'expired' | 'taken' | 'error';

export function ConfirmEmailChangePage() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') ?? '', [params]);
  const [status, setStatus] = useState<Status>('loading');
  const [newEmail, setNewEmail] = useState<string | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!token) { setStatus('invalid'); return; }
    (async () => {
      try {
        const res = await api.auth.confirmEmailChange(token);
        setNewEmail(res.email);
        setStatus('success');
      } catch (err) {
        if (err instanceof ApiException) {
          if (err.payload.code === 'TOKEN_EXPIRED') setStatus('expired');
          else if (err.payload.code === 'INVALID_TOKEN') setStatus('invalid');
          else if (err.payload.code === 'EMAIL_TAKEN') setStatus('taken');
          else setStatus('error');
        } else setStatus('error');
      }
    })();
  }, [token]);

  return (
    <PublicLayout>
      <Card variant="glass" padded className="mx-auto max-w-md text-center">
        {status === 'loading' && (
          <>
            <LogoMark icon={Loader2} accent="grc" size="lg" className="animate-spin" />
            <SectionEyebrow as="div" className="mt-4">Confirmation en cours</SectionEyebrow>
            <p className="mt-2 text-sm text-ink-muted">Vérification de votre lien…</p>
          </>
        )}

        {status === 'success' && (
          <>
            <LogoMark icon={CheckCircle2} accent="grc" size="lg" />
            <h1 className="mt-4 font-display text-xl font-semibold text-ink">Adresse confirmée</h1>
            <Alert variant="success" icon={CheckCircle2} className="mt-4 text-left">
              Votre nouvelle adresse email {newEmail ? <strong>{newEmail}</strong> : ''} est désormais active.
              Utilisez-la pour vos prochaines connexions.
            </Alert>
            <div className="mt-5"><Link to="/login"><Button variant="primary">Se connecter</Button></Link></div>
          </>
        )}

        {(status === 'invalid' || status === 'expired' || status === 'taken' || status === 'error') && (
          <>
            <LogoMark icon={AlertCircle} accent="grc" size="lg" />
            <h1 className="mt-4 font-display text-xl font-semibold text-ink">Confirmation impossible</h1>
            <Alert variant="error" icon={AlertCircle} className="mt-4 text-left">
              {status === 'expired' && 'Ce lien a expiré (valable 2 heures). Relancez la demande de changement depuis votre compte.'}
              {status === 'invalid' && 'Ce lien est invalide ou a déjà été utilisé.'}
              {status === 'taken' && 'Cette adresse est désormais utilisée par un autre compte.'}
              {status === 'error' && 'Une erreur est survenue. Réessayez ou relancez la demande depuis votre compte.'}
            </Alert>
            <div className="mt-5"><Link to="/compte"><Button variant="ghost">Retour à mon compte</Button></Link></div>
          </>
        )}
      </Card>
    </PublicLayout>
  );
}
